/**
 * Mastodon/Pixelfed trending tags service.
 *
 * Calls the Mastodon-compatible /api/v1/trends/tags endpoint on the user's
 * home instance, normalising results to the shared TrendingTopic shape.
 *
 * Both Mastodon and Pixelfed implement this endpoint — callers pass
 * the session's instance_url and access_token directly.
 */

import { isSafeHostname } from '@/lib/apUtils'
import type { TrendingTopic } from '@/services/trendingService'

const FETCH_TIMEOUT_MS = 10_000

/** Raw item shape returned by /api/v1/trends/tags */
interface MastodonTrendingTag {
    name: unknown
    history?: Array<{ day?: unknown }>
    [key: string]: unknown
}

/**
 * Fetch trending hashtags from a Mastodon-compatible instance.
 *
 * Returns an empty array on any error (HTTP or network) — the caller
 * should treat absence of results as "no trending topics available".
 *
 * @param limit - Maximum number of tags to request
 * @param server - Instance base URL (e.g. "https://mastodon.social")
 * @param token - Bearer access token, or null for unauthenticated requests
 */
export async function fetchMastodonTrendingTopics(
    limit: number,
    server: string,
    token: string | null
): Promise<TrendingTopic[]> {
    let hostname: string
    try {
        const parsed = new URL(server)
        if (parsed.protocol !== 'https:') return []
        hostname = parsed.hostname
    } catch {
        return []
    }

    if (!isSafeHostname(hostname)) {
        return []
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const params = new URLSearchParams({ limit: String(limit) })
        const url = `${server}/api/v1/trends/tags?${params}`
        const headers: Record<string, string> = {}
        if (token) {
            headers.Authorization = `Bearer ${token}`
        }

        const res = await fetch(url, { headers, signal: controller.signal })

        if (!res.ok) {
            return []
        }

        const body = (await res.json()) as unknown
        if (!Array.isArray(body)) return []

        return (body as MastodonTrendingTag[])
            .filter(
                (item): item is MastodonTrendingTag & { name: string } =>
                    typeof item?.name === 'string'
            )
            .map(item => ({
                uri: `/hashtag/${encodeURIComponent(item.name)}`,
                displayName: `#${item.name}`,
                createdAt: String(item.history?.[0]?.day ?? ''),
            }))
    } catch {
        // Network error or abort — return empty silently
        return []
    } finally {
        clearTimeout(timeout)
    }
}
